import { useState } from 'react';
import { useAppDispatch } from '../../../../../../../redux/store';
import { openDillToast } from '../../../../../../../utils/helpers';
import {
  addUser,
  getSupplierSummary,
  updateUser,
} from '../../../../../../../redux/services/suppliersService';
import { getUsers } from '../../../../../../../redux/services/authService';
import { DillModal, DillField, DillInput, DillButton } from '../../../../../../general/Dill';
import { AppUser, AppUserRole } from '@dill/dill-shared';
import {
  SupplierUserRolesSection,
  deriveSelectedRoles,
} from '../SupplierUserRolesSection/SupplierUserRolesSection';
import { SupplierUserBranchAccessSection } from '../SupplierUserBranchAccessSection/SupplierUserBranchAccessSection';
import { useSupplierBranchOptions } from '../../../../../../../utils/hooks/useSupplierBranchOptions';

function AddSupplierUserModal({
  open,
  handleClose,
  supplierId,
}: {
  open: boolean;
  handleClose: () => void;
  supplierId: string;
}) {
  const dispatch = useAppDispatch();
  const { branchOptions, isLoadingBranches } = useSupplierBranchOptions();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [roles, setRoles] = useState<AppUserRole[]>([]);
  const [branches, setBranches] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!email || !firstName || !lastName) {
      openDillToast({ type: 'ERROR', message: 'Please fill in all required fields' });
      return;
    }

    const { creditApp, ar } = deriveSelectedRoles(roles);
    if (creditApp === 'NO_ACCESS' && ar === 'NO_ACCESS') {
      openDillToast({ type: 'ERROR', message: 'Please select at least one role' });
      return;
    }

    setLoading(true);
    const result = await dispatch(
      addUser({
        email,
        firstName,
        lastName,
        supplierId,
        roles: roles.length > 0 ? roles : undefined,
        overrideRoles: true,
      })
    );
    setLoading(false);

    if (result.type === 'supplier/addUser/fulfilled') {
      // Branch assignment lives on the user, so it can only be written once the user exists.
      const addedUser = (result.payload as { data?: AppUser } | undefined)?.data;
      if (branches.length > 0 && addedUser?.id) {
        await dispatch(
          updateUser({
            supplierId,
            userId: addedUser.id,
            userDetails: {
              arBranchesBySupplierId: {
                ...(addedUser.arBranchesBySupplierId ?? {}),
                [supplierId]: branches,
              },
            },
          })
        );
      }
      openDillToast({ type: 'SUCCESS', message: 'User added successfully' });
      dispatch(getSupplierSummary({ supplierId }));
      dispatch(
        getUsers({
          data: {
            roles: ['CREDIT_APP_DECISION_MAKER', 'CREDIT_APP_REVIEWER'],
            linkedSupplierIds: [supplierId],
          },
        })
      );
      handleClose();
      setEmail('');
      setFirstName('');
      setLastName('');
      setRoles([]);
      setBranches([]);
    } else {
      openDillToast({ type: 'ERROR', message: result.payload as string });
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Add Supplier User"
      subtitle="Fill in the details below to add a new user"
      maxWidth="540px"
      footer={
        <>
          <DillButton
            onClick={handleClose}
            label="Cancel"
            variant="secondary"
            isDisabled={loading}
          />
          <DillButton onClick={handleSave} label="Add User" isLoading={loading} />
        </>
      }>
      <div className="grid grid-cols-2 gap-3">
        <DillField label="First Name" required>
          <DillInput value={firstName} onChange={setFirstName} placeholder="Enter first name" />
        </DillField>
        <DillField label="Last Name" required>
          <DillInput value={lastName} onChange={setLastName} placeholder="Enter last name" />
        </DillField>
      </div>
      <DillField label="Email" required>
        <DillInput
          value={email}
          onChange={setEmail}
          placeholder="Enter email address"
          type="email"
        />
      </DillField>
      <SupplierUserRolesSection roles={roles} onRolesChange={setRoles} />
      <SupplierUserBranchAccessSection
        branchOptions={branchOptions}
        isLoading={isLoadingBranches}
        branches={branches}
        onBranchesChange={setBranches}
      />
    </DillModal>
  );
}

export default AddSupplierUserModal;
